// =============================================================
// Denticode hero - scrubbable, variant-switchable
// =============================================================

const { useState, useEffect, useRef, useMemo, useCallback } = React;

// ---------- Timeline ----------
// Each event is scheduled at time `t` seconds. Components read currentTime
// and decide what to show.
const TIMELINE = [
  { t: 0.6, kind: 'transcript', id: 't1', speaker: 'provider',
    en: "Starting perio re-eval, upper right quadrant.",
    es: "Iniciando reevaluación periodontal, cuadrante superior derecho." },
  { t: 1.6, kind: 'field', id: 'f-quad', label: 'Quadrant', value: 'Upper Right' },

  { t: 2.6, kind: 'transcript', id: 't2', speaker: 'provider',
    en: "Tooth fourteen, distal-lingual, six millimeters, bleeding on probing.",
    es: "Diente catorce, distal-lingual, seis milímetros, sangrado al sondaje." },
  { t: 3.6, kind: 'field', id: 'f-tooth', label: 'Tooth #', value: '14' },
  { t: 3.9, kind: 'perio', id: 'p-14-dl', tooth: 14, site: 'DL', depth: 6, bop: true },
  { t: 4.5, kind: 'cdt', id: 'c-d4341', code: 'D4341',
    name: 'Periodontal scaling & root planing',
    sub: '4+ teeth per quadrant',
    fee: '$258.00' },

  { t: 5.4, kind: 'transcript', id: 't3', speaker: 'assistant',
    en: "PA radiograph shows bone loss, three to four millimeters.",
    es: "La radiografía PA muestra pérdida ósea, tres a cuatro milímetros." },
  { t: 6.4, kind: 'attach', id: 'a-pa', for: 'D4341', name: 'PA Radiograph', status: 'attached' },

  { t: 7.3, kind: 'transcript', id: 't4', speaker: 'provider',
    en: "Tooth fifteen, mesial, eight, BOP, mobility one.",
    es: "Diente quince, mesial, ocho, sangrado, movilidad uno." },
  { t: 8.2, kind: 'perio', id: 'p-15-m', tooth: 15, site: 'M', depth: 8, bop: true, mobility: 1 },

  { t: 9.0, kind: 'transcript', id: 't5', speaker: 'patient',
    en: "Will I be numb for this part?",
    es: "¿Voy a estar adormecido para esta parte?" },
  { t: 10.0, kind: 'transcript', id: 't6', speaker: 'provider',
    en: "Yes - local anesthetic. Adding bupivacaine 0.5% to the plan.",
    es: "Sí - anestesia local. Añadiendo bupivacaína 0.5% al plan." },
  { t: 11.0, kind: 'cdt', id: 'c-d9215', code: 'D9215',
    name: 'Local anesthesia',
    sub: 'in conjunction with operative procedure',
    fee: '$32.00' },

  { t: 12.5, kind: 'done' }
];
const DURATION = 13.5;

// ---------- Hooks ----------
function useTimeline(playing, setPlaying, setCurrentTime) {
  useEffect(() => {
    if (!playing) return undefined;
    const STEP = 1 / 30; // 30fps target
    const id = setInterval(() => {
      setCurrentTime(prev => {
        const next = prev + STEP;
        if (next >= DURATION) {
          setPlaying(false);
          return DURATION;
        }
        return next;
      });
    }, STEP * 1000);
    return () => clearInterval(id);
  }, [playing, setCurrentTime, setPlaying]);
}

// Pick events that have fired by time `t`
function eventsThrough(t, kind) {
  return TIMELINE.filter(e => e.t <= t && (kind ? e.kind === kind : true));
}

// ---------- Waveform ----------
function Waveform({ playing, t, bars = 56, intensity = 1 }) {
  const seed = useMemo(() => Array.from({ length: bars }, (_, i) =>
    Math.sin(i * 0.42) * 0.5 + Math.sin(i * 1.13 + 1) * 0.3 + Math.sin(i * 0.27 + 2) * 0.2
  ), [bars]);
  return (
    <div className="hd-wave" aria-hidden="true">
      {seed.map((s, i) => {
        const phase = (t * 3 + i * 0.15);
        const base = (s + Math.sin(phase) * 0.4) * intensity;
        const h = playing ? Math.max(4, 4 + Math.abs(base) * 26) : 4 + Math.abs(s) * 10;
        return <div key={i} className="hd-wave-bar" style={{ height: h + 'px', opacity: 0.4 + Math.abs(base) * 0.6 }} />;
      })}
    </div>
  );
}

// ---------- Transcript line ----------
function TranscriptLine({ ev, t, lang }) {
  const visible = ev.t <= t;
  const en = ev.en;
  const es = ev.es;
  // typewriter effect within first 0.6s after event fires
  const elapsed = Math.max(0, t - ev.t);
  const chars = Math.floor((elapsed / 0.6) * en.length);
  const text = lang === 'es' ? es : en.slice(0, Math.min(en.length, Math.max(0, chars)));
  const fullEn = chars >= en.length;
  return (
    <div className={"hd-line " + (visible ? "in" : "")}>
      <span className={"hd-speaker sp-" + ev.speaker}>{ev.speaker}</span>
      <div>
        <span className="hd-text">
          {lang === 'es' ? es : text}
          {lang === 'en' && !fullEn && <span style={{opacity: 0.35}}>▎</span>}
        </span>
        {lang === 'es' && (
          <span className="hd-translation">→ {en}</span>
        )}
      </div>
    </div>
  );
}

// ---------- Variant A: Split transcript → structured ----------
function VariantSplit({ t, playing, lang }) {
  const transcriptEvs = eventsThrough(t, 'transcript');
  const fieldEvs = eventsThrough(t, 'field');
  const cdtEvs = eventsThrough(t, 'cdt');
  const attachEvs = eventsThrough(t, 'attach');

  // Auto-scroll transcript: keep last 4 visible
  const visibleLines = transcriptEvs.slice(-4);

  // Static base fields with placeholders before filling
  const baseFields = [
    { label: 'Quadrant', key: 'f-quad' },
    { label: 'Tooth #', key: 'f-tooth' },
  ];
  const filled = Object.fromEntries(fieldEvs.map(e => [e.id, e]));

  return (
    <div className="hero-demo-frame">
      {/* LEFT pane - transcript */}
      <div className="hd-pane">
        <div className="hd-toolbar">
          <div className="hd-toolbar-title">
            <span className="hd-rec-dot"></span>
            Live session · Op 3
          </div>
          <div className="hd-meta">{lang === 'es' ? 'ES → EN' : 'EN'} · 00:{String(Math.floor(t)).padStart(2,'0')}</div>
        </div>

        <Waveform playing={playing} t={t} />

        <div className="hd-section-label">Transcript</div>
        <div className="hd-transcript">
          {visibleLines.map(ev => (
            <TranscriptLine key={ev.id} ev={ev} t={t} lang={lang} />
          ))}
        </div>
      </div>

      {/* RIGHT pane - structured */}
      <div className="hd-pane">
        <div className="hd-toolbar">
          <div className="hd-toolbar-title">Structured note</div>
          <div className="hd-meta">Perio SRP Template</div>
        </div>

        <div className="hd-template">
          <div className="hd-template-title">
            <span>Periodontal Re-Evaluation</span>
            <span className="tag">PERIO · SRP</span>
          </div>
          {baseFields.map(f => {
            const ev = filled[f.key];
            const justFilled = ev && (t - ev.t) < 1.6;
            return (
              <div className="hd-field" key={f.key}>
                <div className="hd-field-label">{f.label}</div>
                <div className={"hd-field-value " + (ev ? (justFilled ? "just-filled" : "") : "empty")}>
                  {ev ? ev.value : '-'}
                </div>
              </div>
            );
          })}
          {/* Perio cells condensed */}
          <div className="hd-field" style={{gridTemplateColumns: '110px 1fr'}}>
            <div className="hd-field-label">Sites &gt; 4mm</div>
            <div className="hd-field-value" style={{display:'flex', gap:6, flexWrap:'wrap'}}>
              {eventsThrough(t, 'perio').map(p => (
                <span key={p.id} className="chip chip-warning" style={{height:24, fontSize:11.5}}>
                  #{p.tooth} {p.site} · {p.depth}mm {p.bop ? '· BOP' : ''}{p.mobility ? ' · M'+p.mobility : ''}
                </span>
              ))}
              {eventsThrough(t, 'perio').length === 0 && <span style={{color:'var(--text-soft)', fontStyle:'italic', fontSize:13}}>-</span>}
            </div>
          </div>
        </div>

        <div className="hd-cdt-section">
          <div className="hd-section-label">Coding</div>
          <div className="hd-cdt-list">
            {cdtEvs.map(ev => {
              const att = attachEvs.find(a => a.for === ev.code);
              return (
                <div key={ev.id} className="hd-cdt-chip in">
                  <span className="hd-cdt-code">{ev.code}</span>
                  <div style={{display:'flex', flexDirection:'column', gap:2}}>
                    <span style={{fontWeight:600, fontSize:12.5, color:'var(--text)'}}>{ev.name}</span>
                    <span style={{fontSize:11, color:'var(--text-soft)'}}>{ev.sub}</span>
                  </div>
                  {att && <span className="hd-cdt-attach">✓ {att.name}</span>}
                  <span style={{marginLeft:'auto', fontFamily:'var(--font-mono)', fontSize:12, color:'var(--text)', fontWeight:500}}>{ev.fee}</span>
                </div>
              );
            })}
            {cdtEvs.length === 0 && <span style={{color:'var(--text-soft)', fontStyle:'italic', fontSize:13}}>Listening for procedures…</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Variant B: Waveform → speaker-aware transcript → CDT chip (all stay on stage) ----------
function VariantMorph({ t, playing, lang }) {
  // Phases (all three rows STAY once they appear)
  //  0 - 2.4s  : Waveform alone (full attention)
  //  2.4 - 5s  : Transcript line types in below the waveform
  //  5 - 8s    : Chip slides in below transcript; transcript stays
  //  8 +       : Chip attachments resolve
  const p1 = Math.min(1, Math.max(0, (t - 2.4) / 0.8));  // transcript reveal
  const p2 = Math.min(1, Math.max(0, (t - 5.0) / 0.9));  // chip reveal
  const p3 = Math.min(1, Math.max(0, (t - 8.0) / 1.2));  // chip details

  // The speech is structured into ordered segments. We type all segments out
  // character by character as p1 grows; once a segment is fully typed, its
  // background highlight kicks in to show "structured data extracted".
  const segments = lang === 'es'
    ? [
        { t: "Diente ", k: 'plain' },
        { t: "catorce", k: 'tooth' },
        { t: ", ", k: 'plain' },
        { t: "distal-lingual", k: 'site' },
        { t: ", ", k: 'plain' },
        { t: "seis", k: 'depth' },
        { t: " milímetros, sangrado.", k: 'plain' },
      ]
    : [
        { t: "Tooth ", k: 'plain' },
        { t: "fourteen", k: 'tooth' },
        { t: ", ", k: 'plain' },
        { t: "distal-lingual", k: 'site' },
        { t: ", ", k: 'plain' },
        { t: "six", k: 'depth' },
        { t: " millimeters, bleeding.", k: 'plain' },
      ];
  const totalLen = segments.reduce((s, x) => s + x.t.length, 0);
  // Slow the typing a bit so it visibly streams
  const charsRevealed = Math.floor(p1 * (totalLen + 4));
  let consumed = 0;
  const renderedSegments = segments.map((seg, i) => {
    const segStart = consumed;
    const segEnd = consumed + seg.t.length;
    consumed = segEnd;
    const visibleChars = Math.max(0, Math.min(seg.t.length, charsRevealed - segStart));
    if (visibleChars === 0) return null;
    const text = seg.t.slice(0, visibleChars);
    const fullyTyped = visibleChars === seg.t.length;
    if (seg.k === 'plain') return <span key={i}>{text}</span>;
    return (
      <span key={i} className={"hd-morph-hl " + (fullyTyped ? "hl-done" : "hl-typing")}>
        {text}
      </span>
    );
  });
  const stillTyping = p1 > 0 && charsRevealed < totalLen;

  // Once the chip appears, gently dim the waveform but keep it visible
  const wavOpacity = p2 > 0 ? 0.55 : 1;
  const transcriptOpacity = p1;

  return (
    <div className="hd-morph">
      <div className="hd-morph-stack">
        {/* Row 1 - Voice / waveform (always visible) */}
        <div className="hd-morph-row" style={{ opacity: wavOpacity }}>
          <div className="hd-morph-row-l">
            <div className="hd-morph-label"><span className="hd-rec-dot"></span> Voice</div>
            <div className="hd-morph-sub">Speaker-aware</div>
          </div>
          <div className="hd-morph-row-r">
            <Waveform playing={playing} t={t} intensity={p2 > 0 ? 0.7 : 1.3} bars={64} />
          </div>
        </div>

        {/* Connector → */}
        <div className="hd-morph-arrow" style={{ opacity: Math.min(1, p1 * 1.5) }}>
          <svg width="12" height="22" viewBox="0 0 12 22" aria-hidden="true">
            <path d="M6 0 L6 16 M2 14 L6 19 L10 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Row 2 - Transcript line w/ speaker chip */}
        <div className="hd-morph-row hd-morph-row--transcript"
             style={{
               opacity: transcriptOpacity,
               transform: `translateY(${(1 - p1) * 10}px)`,
             }}>
          <div className="hd-morph-row-l">
            <span className="hd-speaker sp-provider">provider</span>
          </div>
          <div className="hd-morph-row-r hd-morph-transcript">
            {renderedSegments}
            {stillTyping && <span className="hd-morph-caret">▎</span>}
          </div>
        </div>

        {/* Connector ↓ */}
        <div className="hd-morph-arrow" style={{ opacity: Math.min(1, p2 * 1.5) }}>
          <svg width="12" height="22" viewBox="0 0 12 22" aria-hidden="true">
            <path d="M6 0 L6 16 M2 14 L6 19 L10 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Row 3 - Code chip */}
        <div className="hd-morph-row hd-morph-row--chip"
             style={{
               opacity: p2,
               transform: `translateY(${(1 - p2) * 18}px) scale(${0.96 + p2 * 0.04})`,
             }}>
          <div className="hd-morph-row-l">
            <div className="hd-morph-label" style={{color:'var(--teal-deep)'}}>
              <span style={{width:8,height:8,borderRadius:'50%',background:'var(--teal)'}}></span> Code
            </div>
            <div className="hd-morph-sub">Attachments resolved</div>
          </div>
          <div className="hd-morph-row-r">
            <div className="hd-morph-chip">
              <div className="hd-morph-chip-code">
                <div style={{fontFamily:'var(--font-mono)', fontSize:22, fontWeight:700, letterSpacing:'-0.01em'}}>D4341</div>
                <div style={{fontSize:10, opacity:0.85, letterSpacing:'0.04em', textTransform:'uppercase'}}>CDT 2026</div>
              </div>
              <div className="hd-morph-chip-body">
                <div style={{fontSize:14, fontWeight:600, color:'var(--text)'}}>Periodontal scaling and root planing</div>
                <div style={{fontSize:12.5, color:'var(--text-muted)'}}>Four or more teeth per quadrant</div>
                <div style={{display:'flex', gap:8, marginTop:6, alignItems:'center', flexWrap:'wrap',
                             opacity: Math.min(1, p3 * 1.4),
                             transform: `translateY(${(1 - Math.min(1, p3 * 1.4)) * 4}px)` }}>
                  <span className="chip chip-success" style={{height:24, fontSize:11}}>✓ Perio chart</span>
                  <span className="chip chip-success" style={{height:24, fontSize:11}}>✓ PA radiograph</span>
                  <span style={{marginLeft:'auto', fontFamily:'var(--font-mono)', fontSize:13, fontWeight:600, color:'var(--text)'}}>$258.00</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Three-step progress */}
      <div className="hd-morph-progress">
        {[
          { label:'Voice', sub:'Speaker-aware', active: t >= 0, done: p1 >= 1 },
          { label:'Transcript', sub:'Structured language', active: p1 > 0, done: p2 >= 1 },
          { label:'CDT code', sub:'Attachments resolved', active: p2 > 0, done: p3 >= 1 },
        ].map((s, i) => (
          <div key={i} className="hd-morph-progress-step" style={{ opacity: s.active ? 1 : 0.35 }}>
            <div className="hd-morph-progress-bar"
                 style={{
                   background: s.done ? 'var(--teal)'
                              : s.active ? 'linear-gradient(to right, var(--teal), var(--border))'
                              : 'var(--border)',
                 }} />
            <div style={{fontSize:13, fontWeight:600, color:'var(--text)', marginTop:6}}>{s.label}</div>
            <div style={{fontSize:11.5, color:'var(--text-soft)'}}>{s.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Variant C: Live Lab session preview (transcript + perio chart) ----------
function VariantSession({ t, playing, lang }) {
  const transcriptEvs = eventsThrough(t, 'transcript').slice(-3);
  const cdtEvs = eventsThrough(t, 'cdt');
  const perioEvs = eventsThrough(t, 'perio');

  // Build a tiny perio chart matrix (teeth 13-16, 3 sites each: M, B, D)
  const teeth = [13, 14, 15, 16];
  const sites = ['M', 'B', 'D'];
  const cellFor = (tooth, site) => {
    return perioEvs.find(p => p.tooth === tooth && (
      site === 'M' && p.site === 'M' ||
      site === 'D' && p.site === 'DL' ||
      site === 'B' && p.site === 'B'
    ));
  };

  return (
    <div className="hd-session">
      {/* Left: transcript */}
      <div className="hd-pane">
        <div className="hd-toolbar">
          <div className="hd-toolbar-title"><span className="hd-rec-dot"></span> Lab · Op 3</div>
          <div className="hd-meta">00:{String(Math.floor(t)).padStart(2,'0')}</div>
        </div>
        <Waveform playing={playing} t={t} />
        <div className="hd-section-label">Live transcript</div>
        <div className="hd-transcript">
          {transcriptEvs.map(ev => (
            <TranscriptLine key={ev.id} ev={ev} t={t} lang={lang} />
          ))}
        </div>
      </div>

      {/* Right: perio mini-chart + CDT */}
      <div className="hd-pane">
        <div className="hd-toolbar">
          <div className="hd-toolbar-title">Perio chart · Upper Right</div>
          <div className="hd-meta">Voice-driven</div>
        </div>

        <div style={{
          background:'var(--surface-subtle)',
          border:'1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          padding: 14,
        }}>
          <div style={{display:'grid', gridTemplateColumns:'40px repeat(4, 1fr)', gap:6, alignItems:'center', marginBottom: 8}}>
            <div></div>
            {teeth.map(n => <div key={n} style={{textAlign:'center', fontSize:11, fontWeight:600, color:'var(--text-muted)', fontFamily:'var(--font-mono)'}}>#{n}</div>)}
          </div>
          {sites.map(site => (
            <div key={site} style={{display:'grid', gridTemplateColumns:'40px repeat(4, 1fr)', gap:6, alignItems:'center', marginBottom: 6}}>
              <div style={{fontSize:11, color:'var(--text-soft)', fontWeight:600}}>{site}</div>
              {teeth.map(n => {
                const cell = cellFor(n, site);
                const recent = cell && (t - cell.t) < 1.4;
                let bg = '#fff';
                let color = 'var(--text-soft)';
                let content = '-';
                if (cell) {
                  content = cell.depth + (cell.bop ? '•' : '');
                  if (cell.depth >= 6) { bg = '#FEE2E2'; color = '#B91C1C'; }
                  else if (cell.depth >= 4) { bg = '#FEF3C7'; color = '#B45309'; }
                  else { bg = '#DCFCE7'; color = '#15803D'; }
                }
                return (
                  <div key={n} style={{
                    height: 32, borderRadius: 8, background: bg, color,
                    border: '1px solid var(--border)',
                    display:'grid', placeItems:'center',
                    fontFamily:'var(--font-mono)', fontSize:13, fontWeight:600,
                    boxShadow: recent ? '0 0 0 2px var(--teal)' : 'none',
                    transition: 'box-shadow 0.6s ease, background 0.4s ease',
                  }}>{content}</div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Stats */}
        <div style={{display:'flex', gap:8, marginTop: 6, flexWrap:'wrap'}}>
          <span className="chip chip-danger">Sites ≥ 6mm: <strong style={{marginLeft:4}}>{perioEvs.filter(p=>p.depth>=6).length}</strong></span>
          <span className="chip chip-warning">Sites 4–5mm: <strong style={{marginLeft:4}}>{perioEvs.filter(p=>p.depth>=4 && p.depth<6).length}</strong></span>
          <span className="chip">BOP%: <strong style={{marginLeft:4}}>{perioEvs.length ? Math.round(perioEvs.filter(p=>p.bop).length/perioEvs.length*100) : 0}%</strong></span>
        </div>

        <div className="hd-section-label">Suggested codes</div>
        <div className="hd-cdt-list">
          {cdtEvs.map(ev => (
            <div key={ev.id} className="hd-cdt-chip in">
              <span className="hd-cdt-code">{ev.code}</span>
              <div style={{display:'flex', flexDirection:'column', gap:2}}>
                <span style={{fontWeight:600, fontSize:12.5, color:'var(--text)'}}>{ev.name}</span>
              </div>
              <span style={{marginLeft:'auto', fontFamily:'var(--font-mono)', fontSize:12, fontWeight:500}}>{ev.fee}</span>
            </div>
          ))}
          {cdtEvs.length === 0 && <span style={{color:'var(--text-soft)', fontStyle:'italic', fontSize:13}}>Awaiting first procedure…</span>}
        </div>
      </div>
    </div>
  );
}

// ---------- Variant D: Bilingual ----------
function VariantBilingual({ t, playing, lang }) {
  // Forces Spanish on left, English on right regardless of `lang`
  const transcriptEvs = eventsThrough(t, 'transcript').slice(-4);
  const fieldEvs = eventsThrough(t, 'field');
  const cdtEvs = eventsThrough(t, 'cdt');

  return (
    <div className="hd-bilingual">
      <div style={{display:'flex', flexDirection:'column', gap:18}}>
        <div className="hd-toolbar">
          <div className="hd-toolbar-title"><span className="hd-rec-dot"></span> Source · Español</div>
          <div className="hd-meta">ES</div>
        </div>
        <Waveform playing={playing} t={t} />
        <div className="hd-section-label">Heard</div>
        <div className="hd-transcript">
          {transcriptEvs.map(ev => (
            <div key={ev.id} className="hd-line in">
              <span className={"hd-speaker sp-" + ev.speaker}>{ev.speaker}</span>
              <div>
                <span className="hd-text">{ev.es}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{display:'flex', flexDirection:'column', gap:18, position:'relative'}}>
        <div className="hd-toolbar">
          <div className="hd-toolbar-title">Note · English</div>
          <div className="hd-meta">EN · auto-translated</div>
        </div>
        <div style={{height: 32}}></div>
        <div className="hd-section-label">Auto-translated</div>
        <div className="hd-transcript">
          {transcriptEvs.map(ev => (
            <div key={ev.id} className="hd-line in">
              <span className={"hd-speaker sp-" + ev.speaker}>{ev.speaker}</span>
              <div>
                <span className="hd-text">{ev.en}</span>
              </div>
            </div>
          ))}
        </div>
        {cdtEvs.length > 0 && (
          <div style={{display:'flex', gap:6, flexWrap:'wrap', marginTop:6}}>
            {cdtEvs.map(ev => (
              <span key={ev.id} className="chip chip-teal" style={{height:28, fontFamily:'var(--font-mono)'}}>{ev.code}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Hero ----------
function Hero() {
  const TWEAK_DEFAULTS = (window.__DC_TWEAK_DEFAULTS || { heroLang: 'en' });
  const [tweaks, setTweak] = window.useTweaks(TWEAK_DEFAULTS);
  const lang = tweaks.heroLang || 'en';

  // Auto-looping playhead. Always plays. Resets to 0 when finished, then re-runs.
  const [currentTime, setCurrentTime] = useState(0);
  useEffect(() => {
    const STEP = 1 / 30;
    const HOLD_AT_END = 1.6; // hold the final state for a beat before looping
    const id = setInterval(() => {
      setCurrentTime(prev => {
        const next = prev + STEP;
        if (next >= DURATION + HOLD_AT_END) return 0;
        return next;
      });
    }, STEP * 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <>
      <VariantMorph t={Math.min(currentTime, DURATION)} playing={true} lang={lang} />

      {window.TweaksPanel && (
        <window.TweaksPanel title="Hero Tweaks">
          <window.TweakSection label="Language">
            <window.TweakRadio
              label="Source"
              value={lang}
              onChange={(v) => setTweak('heroLang', v)}
              options={[
                { label: 'English', value: 'en' },
                { label: 'Español', value: 'es' },
              ]}
            />
          </window.TweakSection>
        </window.TweaksPanel>
      )}
    </>
  );
}

// Mount
const heroRoot = document.getElementById('hero-mount');
if (heroRoot) {
  ReactDOM.createRoot(heroRoot).render(<Hero />);
}
